import { memo, useState, useEffect } from 'react';

import { StratKey } from 'features/drilldown/Stratifications/startifications.config';
import { StratCard } from 'features/drilldown/Stratifications/StratCard';
import { StratsSelection } from 'features/drilldown/Stratifications/StratsSelection';
import { SpinnerComponent } from 'ui-kit';

import { gql, useQuery } from '@apollo/client';

import { AssetClass } from '__generated__/globalTypes';
import { CarveDropdown, CarveOption } from './CarveDropdown';
import { GetDealStrats, GetDealStratsVariables } from './gql/__generated__/inquiryQueries';
import { GET_DEAL_STRATS_QUERY, GET_DEAL_PERFORMANCE_QUERY } from './gql/inquiryQueries';

const StratsCardFragments = {
  TimelineCard: gql`
    fragment StratsCard on TimelineCard {
      __typename
      ... on StratificationsCard {
        assetClass: asset_class
        allow_filtering
      }
    }
  `,
};

const useStratCard = (props: {
  dealId: string;
  assetClass: AssetClass;
  selectedCarve?: CarveOption | null;
}) => {
  const carveQueryFilters = props.selectedCarve?.carveFilters || [];

  const { data, loading } = useQuery<GetDealStrats, GetDealStratsVariables>(GET_DEAL_STRATS_QUERY, {
    variables: {
      id: props.dealId,
    },
  });

  // Use GET_DEAL_PERFORMANCE_QUERY to get performanceData with carve filters
  const { data: perfData } = useQuery<GetDealStrats, GetDealStratsVariables>(
    GET_DEAL_PERFORMANCE_QUERY,
    {
      variables: {
        id: props.dealId,
        filters: carveQueryFilters,
      } as any,
      fetchPolicy: 'no-cache',
    },
  );

  const [strats, setStrats] = useState<StratKey[]>([]);

  // Use filtered performance data if carve is selected, otherwise use original
  const performance = props.selectedCarve
    ? perfData?.deal?.performanceData
    : data?.deal?.performanceData;
  // Set default stratifications when data loads
  useEffect(() => {
    if (performance && strats.length === 0) {
      // Set some default stratifications to show
      setStrats(['strat_age_months', 'strat_borrower_credit_score', 'strat_current_balance_cents']);
    }
  }, [performance, strats.length]);

  return {
    strats,
    setStrats,
    performance,
    loading,
  };
};

const StratCardBodyComponent = (props: {
  dealId: string;
  assetClass: AssetClass;
  allow_filtering?: boolean;
}) => {
  const [selectedCarve, setSelectedCarve] = useState<CarveOption | null>(null);

  const { strats, setStrats, performance, loading } = useStratCard({
    ...props,
    selectedCarve,
  });
  const stratsBlock = performance ? (
    <>
      <div className="mb-3 flex items-center justify-between">
        <CarveDropdown
          dealId={props.dealId}
          value={selectedCarve?.id}
          onValueChange={setSelectedCarve}
          className="min-w-[200px]"
          allowFiltering={props.allow_filtering}
        />
      </div>
      <StratsSelection selectedStrats={strats} onSelectedStratsChange={setStrats} />
      <div className="mt-2 flex flex-wrap gap-2 [&>*]:max-h-[550px] [&>*]:flex-[1_1_500px]">
        {strats.map((strat) => (
          <StratCard
            key={strat}
            strat={strat}
            assetClass={props.assetClass}
            summary={performance}
            avgSummary={performance}
          />
        ))}
      </div>
    </>
  ) : null;

  return (
    <>
      {stratsBlock}
      <SpinnerComponent loading={loading} positionRelative={true} />
    </>
  );
};

const StratCardBody = memo(StratCardBodyComponent);
export { StratCardBody, StratsCardFragments };
